// ============================================================================
// CHANGO V5.0 — D5 Client-side margin preview
// ============================================================================
// Mirrors the server's marginCalc.js so the wizard can show live totals
// without a round-trip. The SERVER IS THE SOURCE OF TRUTH — this file only
// drives the UI. On save/submit, the backend recomputes and the response
// overwrites any UI-local numbers.
//
// Intermediate arithmetic stays in raw RMB numbers; only public display fields
// are rounded. Keep this file in lockstep with the server engine.
// ============================================================================

export const SUPPORTED_CURRENCIES = Object.freeze([
  'CNY',
  'USD', 'EUR', 'JPY', 'HKD', 'GBP',
  'AUD', 'CAD', 'SGD', 'CHF', 'NZD',
  'MOP', 'KRW', 'THB', 'MYR', 'RUB', 'ZAR', 'PHP',
  'SEK', 'DKK', 'NOK', 'TRY', 'MXN', 'BRL',
  'AED', 'SAR', 'PLN', 'HUF', 'IDR', 'INR', 'TWD',
  'BND', 'CZK', 'ILS', 'KHR', 'KWD', 'MNT', 'NPR',
  'PKR', 'QAR', 'RSD', 'VND'
]);
export const CURRENCY_LABEL = Object.freeze({
  CNY: '人民币 (¥)',
  USD: '美元 ($)',
  EUR: '欧元 (€)',
  JPY: '日元 (¥)',
  HKD: '港币 (HK$)',
  GBP: '英镑 (£)',
  AUD: '澳元 (A$)',
  CAD: '加元 (C$)',
  SGD: '新加坡元 (S$)',
  CHF: '瑞士法郎 (CHF)',
  NZD: '新西兰元 (NZ$)',
  MOP: '澳门元 (MOP$)',
  KRW: '韩元 (₩)',
  THB: '泰铢 (฿)',
  MYR: '林吉特 (RM)',
  RUB: '卢布 (₽)',
  ZAR: '南非兰特 (R)',
  PHP: '菲律宾比索 (₱)',
  SEK: '瑞典克朗 (kr)',
  DKK: '丹麦克朗 (kr)',
  NOK: '挪威克朗 (kr)',
  TRY: '土耳其里拉 (₺)',
  MXN: '墨西哥比索 ($)',
  BRL: '巴西雷亚尔 (R$)',
  AED: '阿联酋迪拉姆',
  SAR: '沙特里亚尔',
  PLN: '波兰兹罗提',
  HUF: '匈牙利福林',
  IDR: '印尼卢比 (Rp)',
  INR: '印度卢比 (₹)',
  TWD: '新台币 (NT$)',
  BND: '文莱元',
  CZK: '捷克克朗',
  ILS: '以色列谢克尔',
  KHR: '柬埔寨瑞尔',
  KWD: '科威特第纳尔',
  MNT: '蒙古图格里克',
  NPR: '尼泊尔卢比',
  PKR: '巴基斯坦卢比',
  QAR: '卡塔尔里亚尔',
  RSD: '塞尔维亚第纳尔',
  VND: '越南盾'
});

function isFiniteNumber(x) { return typeof x === 'number' && Number.isFinite(x); }

const REBATE_OWNER_BY_TREATMENT = Object.freeze({
  none: 'none',
  factory_node: 'factory',
  trade_node: 'trade',
  entity_node: 'entity'
});

function normalizeRebateTreatment(value, owner) {
  if (value === 'none' || value === 'factory_node' || value === 'trade_node' || value === 'entity_node') return value;
  if (owner === 'none') return 'none';
  if (owner === 'trade_company' || owner === 'trade') return 'trade_node';
  if (owner === 'overseas_entity' || owner === 'entity') return 'entity_node';
  return 'factory_node';
}

const LAYER_C_FIELDS = Object.freeze([
  'platform_commission_pct',
  'payment_fee_pct',
  'ad_rate_pct',
  'return_rate_pct',
  'fba_fee_per_unit_rmb',
  'overseas_storage_rmb',
  'after_sales_cost_rmb',
  'channel_tax_rmb',
  'settlement_fee_rmb',
  'inventory_capital_cost_rmb',
  'other_channel_cost_rmb'
]);

const LAYER_B_FIELDS = Object.freeze([
  'sea_freight_rmb',
  'insurance_rmb',
  'customs_declaration_rmb',
  'port_misc_rmb',
  'customs_clearance_rmb',
  'duty_rmb',
  'inland_transport_rmb',
  'inspection_rmb',
  'payment_collection_fee_rmb',
  'financing_cost_rmb',
  'sales_commission_rmb',
  'risk_reserve_rmb',
  'other_trade_cost_rmb'
]);

const MANUFACTURING_FEE_MODES = Object.freeze([
  'total_allocation',
  'per_unit',
  'percentage',
  'manual_override'
]);

const TRADE_COST_RULES = Object.freeze([
  'fixed_order',
  'sales_pct',
  'cost_pct',
  'destination_rule',
  'manual'
]);

const CHANNEL_COST_BASIS_MODES = Object.freeze([
  'inherit_trade',
  'manual'
]);

const ENTITY_EXPENSE_RATIO_METHODS = Object.freeze(['sales_share', 'order_qty', 'entity_usage']);

const LAYER_A_DEFAULT_MATERIALS = Object.freeze([
  {
    name: '主材',
    spec_source: '',
    consumption: 1,
    unit: '件',
    tax_included_unit_price_rmb: 0,
    input_vat_rate_pct: 13
  }
]);

const LAYER_A_DEFAULT_PROCESSES = Object.freeze([
  {
    name: '工序',
    standard_hours: 0,
    labor_rate_rmb_per_hour: 0,
    equipment_energy_rmb: 0,
    input_vat_rate_pct: 0
  }
]);

const LAYER_A_DEFAULT_FEES = Object.freeze([
  {
    label: '制造费用',
    allocation_rule: '按件',
    amount_rmb: 0,
    input_vat_rate_pct: 0
  }
]);

function hasPositiveValue(obj, fields) {
  return fields.some((field) => Number(obj?.[field] || 0) > 0);
}

function clampPct(raw) {
  const value = Number(raw ?? 0);
  if (!isFiniteNumber(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function clampSignedRebateRate(raw) {
  const value = Number(raw ?? 0);
  if (!isFiniteNumber(value)) return 0;
  return Math.max(-1, Math.min(1, value));
}

function safeMoney(raw) {
  const value = Number(raw ?? 0);
  return isFiniteNumber(value) && value > 0 ? value : 0;
}

function safeText(raw, fallback = '') {
  return String(raw ?? fallback).trim();
}

function normalizeManufacturingFeeMode(raw) {
  const value = safeText(raw).toLowerCase();
  if (!value) return null;
  const aliases = {
    total_allocation: 'total_allocation',
    total: 'total_allocation',
    total_alloc: 'total_allocation',
    total_allocate: 'total_allocation',
    total_amount: 'total_allocation',
    batch_total: 'total_allocation',
    unit_input: 'per_unit',
    per_unit: 'per_unit',
    unit: 'per_unit',
    amount_per_unit: 'per_unit',
    unit_amount: 'per_unit',
    percentage: 'percentage',
    ratio: 'percentage',
    ratio_allocation: 'percentage',
    percent: 'percentage',
    pct: 'percentage',
    manual: 'manual_override',
    manual_override: 'manual_override',
    override: 'manual_override'
  };
  return aliases[value] || null;
}

function normalizeTradeCostRule(raw) {
  const value = safeText(raw).toLowerCase();
  const aliases = {
    fixed_order: 'fixed_order',
    fixed: 'fixed_order',
    fixed_amount: 'fixed_order',
    amount: 'fixed_order',
    order_fixed: 'fixed_order',
    sales_pct: 'sales_pct',
    sales_ratio: 'sales_pct',
    sales_share: 'sales_pct',
    revenue_pct: 'sales_pct',
    revenue_ratio: 'sales_pct',
    cost_pct: 'cost_pct',
    cost_ratio: 'cost_pct',
    base_cost_pct: 'cost_pct',
    destination_rule: 'destination_rule',
    destination: 'destination_rule',
    port_rule: 'destination_rule',
    manual: 'manual',
    manual_override: 'manual',
    override: 'manual'
  };
  const normalized = aliases[value] || 'fixed_order';
  return TRADE_COST_RULES.includes(normalized) ? normalized : 'fixed_order';
}

function normalizeChannelCostBasisMode(raw) {
  const value = safeText(raw).toLowerCase();
  const aliases = {
    inherit_trade: 'inherit_trade',
    inherit: 'inherit_trade',
    trade: 'inherit_trade',
    trade_cost: 'inherit_trade',
    previous: 'inherit_trade',
    previous_cost: 'inherit_trade',
    manual: 'manual',
    manual_basis: 'manual',
    manual_cost: 'manual',
    override: 'manual'
  };
  const normalized = aliases[value] || 'inherit_trade';
  return CHANNEL_COST_BASIS_MODES.includes(normalized) ? normalized : 'inherit_trade';
}

function normalizeEntityExpenseMethod(raw) {
  const value = safeText(raw).toLowerCase();
  const aliases = {
    manual: 'manual',
    manual_override: 'manual',
    override: 'manual',
    fixed: 'manual',
    fixed_amount: 'manual',
    direct: 'manual',
    ratio: 'sales_share',
    sales_share: 'sales_share',
    sales_ratio: 'sales_share',
    sales_pct: 'sales_share',
    revenue_pct: 'sales_share',
    order_qty: 'order_qty',
    qty: 'order_qty',
    quantity: 'order_qty',
    quantity_share: 'order_qty',
    entity_usage: 'entity_usage',
    entity_usage_pct: 'entity_usage',
    usage: 'entity_usage',
    usage_pct: 'entity_usage',
    auto: 'auto_tax',
    auto_tax: 'auto_tax',
    tax_rule: 'auto_tax',
    local_tax: 'auto_tax'
  };
  return aliases[value] || (value || 'manual');
}

function explicitLayerAAmount(row) {
  return safeMoney(row?.amount_rmb ?? row?.tax_inclusive_amount_rmb ?? row?.total_amount_rmb);
}

function materialAmount(row) {
  const explicit = explicitLayerAAmount(row);
  if (explicit > 0) return explicit;
  return row.consumption * row.tax_included_unit_price_rmb;
}

function processAmount(row) {
  const explicit = explicitLayerAAmount(row);
  if (explicit > 0) return explicit;
  return (row.standard_hours * row.labor_rate_rmb_per_hour) + row.equipment_energy_rmb;
}

function manufacturingFeeBasisAmount(layerAInputs, row, materialTotal, processTotal) {
  const basis = safeText(
    row?.manufacturing_fee_allocation_basis
      || layerAInputs?.manufacturing_fee_allocation_basis
      || 'quote_qty'
  );
  if (basis === 'material_cost') return materialTotal;
  if (basis === 'process_cost' || basis === 'standard_hours') return processTotal;
  return materialTotal + processTotal;
}

function manufacturingFeeAmount(row, layerAInputs, basisCostPerUnit, totalQty) {
  const mode = normalizeManufacturingFeeMode(
    row.manufacturing_fee_mode || layerAInputs.manufacturing_fee_mode || 'per_unit'
  ) || 'per_unit';
  const qty = totalQty > 0 ? totalQty : 1;
  if (mode === 'total_allocation') {
    return (row.total_amount_rmb || row.amount_rmb || 0) / qty;
  }
  if (mode === 'percentage') {
    return basisCostPerUnit * ((row.ratio_pct || 0) / 100);
  }
  if (mode === 'manual_override') {
    return row.amount_rmb || row.unit_amount_rmb || 0;
  }
  return row.unit_amount_rmb || row.amount_rmb || 0;
}

function normalizeOptionalBoolean(raw, fallback = true) {
  if (raw == null) return fallback;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  if (typeof raw === 'string') {
    const value = raw.trim().toLowerCase();
    if (['false', '0', 'no', 'off'].includes(value)) return false;
    if (['true', '1', 'yes', 'on'].includes(value)) return true;
  }
  return fallback;
}

function normalizeLayerA(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const materialRows = Array.isArray(raw.material_items) && raw.material_items.length > 0
    ? raw.material_items
    : (Array.isArray(raw.materials) && raw.materials.length > 0 ? raw.materials : LAYER_A_DEFAULT_MATERIALS);
  const processRows = Array.isArray(raw.process_items) && raw.process_items.length > 0
    ? raw.process_items
    : (Array.isArray(raw.processes) && raw.processes.length > 0 ? raw.processes : LAYER_A_DEFAULT_PROCESSES);
  const feeRows = Array.isArray(raw.manufacturing_fees) && raw.manufacturing_fees.length > 0
    ? raw.manufacturing_fees
    : LAYER_A_DEFAULT_FEES;

  const material_items = materialRows.map((row, idx) => ({
    name: safeText(row?.name, idx === 0 ? '主材' : `物料 ${idx + 1}`).slice(0, 80),
    spec_source: safeText(row?.spec_source ?? row?.source ?? row?.spec).slice(0, 120),
    consumption: safeMoney(row?.consumption ?? row?.qty ?? row?.quantity ?? row?.unit_qty ?? 1),
    unit: safeText(row?.unit, '件').slice(0, 20),
    tax_included_unit_price_rmb: safeMoney(
      row?.tax_included_unit_price_rmb
        ?? row?.tax_inclusive_unit_price_rmb
        ?? row?.unit_price_rmb
        ?? row?.amount_rmb
    ),
    amount_rmb: explicitLayerAAmount(row),
    input_vat_rate_pct: clampPct(row?.input_vat_rate_pct ?? 13)
  }));
  const process_items = processRows.map((row, idx) => ({
    name: safeText(row?.name, idx === 0 ? '工序' : `工序 ${idx + 1}`).slice(0, 80),
    standard_hours: safeMoney(row?.standard_hours ?? row?.hours ?? row?.work_hours),
    labor_rate_rmb_per_hour: safeMoney(
      row?.labor_rate_rmb_per_hour
        ?? row?.labor_rate_rmb
        ?? row?.hourly_rate_rmb
    ),
    equipment_energy_rmb: safeMoney(row?.equipment_energy_rmb),
    amount_rmb: explicitLayerAAmount(row),
    input_vat_rate_pct: clampPct(row?.input_vat_rate_pct)
  }));
  const manufacturing_fees = feeRows.map((row, idx) => ({
    label: safeText(row?.label, idx === 0 ? '制造费用' : `费用 ${idx + 1}`).slice(0, 80),
    allocation_rule: safeText(row?.allocation_rule, '按件').slice(0, 40),
    manufacturing_fee_mode: normalizeManufacturingFeeMode(row?.manufacturing_fee_mode ?? row?.mode),
    manufacturing_fee_allocation_basis: safeText(
      row?.manufacturing_fee_allocation_basis ?? row?.allocation_basis
    ).slice(0, 80),
    total_amount_rmb: safeMoney(row?.total_amount_rmb ?? row?.total_allocation_rmb ?? row?.fee_total_rmb),
    unit_amount_rmb: safeMoney(row?.unit_amount_rmb ?? row?.unit_fee_rmb ?? row?.amount_per_unit_rmb),
    ratio_pct: clampPct(row?.ratio_pct ?? row?.allocation_pct),
    amount_rmb: safeMoney(row?.amount_rmb ?? row?.tax_inclusive_amount_rmb),
    input_vat_rate_pct: clampPct(row?.input_vat_rate_pct)
  }));
  const manufacturingFeeMode = normalizeManufacturingFeeMode(raw.manufacturing_fee_mode ?? raw.mode)
    || 'per_unit';
  const legacyFactoryRebateEligible = Boolean(
    raw.factory_rebate_eligible ?? raw.factory_handles_rebate ?? raw.eligible ?? false
  );
  const rebateTreatment = normalizeRebateTreatment(
    raw.rebate_treatment,
    raw.rebate_owner ?? (legacyFactoryRebateEligible ? 'factory' : undefined)
  );
  const factoryRebateEligible = rebateTreatment === 'factory_node';
  const norm = {
    quote_logic: ['reverse', 'mixed'].includes(raw.quote_logic) ? raw.quote_logic : 'forward',
    factory_business_type: ['direct_export', 'domestic_trade', 'external_factory']
      .includes(raw.factory_business_type) ? raw.factory_business_type : 'direct_export',
    output_term: ['EXW', 'FOB'].includes(raw.output_term) ? raw.output_term : 'EXW',
    factory_rebate_mode: safeText(raw.factory_rebate_mode ?? raw.rebate_mode).slice(0, 40),
    factory_rebate_eligible: factoryRebateEligible,
    factory_handles_rebate: factoryRebateEligible,
    rebate_treatment: rebateTreatment,
    rebate_owner: REBATE_OWNER_BY_TREATMENT[rebateTreatment],
    factory_rebate_rate: clampSignedRebateRate(raw.factory_rebate_rate ?? raw.rebate_rate),
    use_factory_quote_as_cost: normalizeOptionalBoolean(
      raw.use_factory_quote_as_cost
        ?? raw.inherit_factory_quote_as_cost
        ?? raw.carry_factory_quote_to_cost
        ?? raw.factory_quote_as_cost,
      true
    ),
    small_batch_uplift_pct: clampPct(raw.small_batch_uplift_pct),
    factory_margin_pct: Math.min(95, clampPct(raw.factory_margin_pct)),
    capital_days: safeMoney(raw.capital_days || 45),
    manufacturing_fee_mode: manufacturingFeeMode,
    manufacturing_fee_allocation_basis: safeText(
      raw.manufacturing_fee_allocation_basis ?? raw.allocation_basis
    ).slice(0, 80),
    material_items,
    process_items,
    manufacturing_fees
  };
  const hasRows = [...material_items, ...process_items, ...manufacturing_fees].some((row) =>
    Object.values(row).some((value) => typeof value === 'number' && value > 0)
  );
  return hasRows || norm.small_batch_uplift_pct > 0 || norm.factory_margin_pct > 0
    || norm.factory_rebate_eligible || norm.factory_rebate_rate !== 0
    || raw.rebate_treatment || raw.rebate_owner
    || raw.quote_logic || raw.factory_business_type || raw.output_term
    ? norm
    : null;
}

function normalizeLayerC(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const costBasisMode = normalizeChannelCostBasisMode(raw.cost_basis_mode);
  const manualBasisCostPerUnitRmb = safeMoney(
    raw.manual_basis_cost_per_unit_rmb ?? raw.manual_cost_basis_per_unit_rmb
  );
  const norm = {
    cost_basis_mode: costBasisMode,
    manual_basis_cost_per_unit_rmb: manualBasisCostPerUnitRmb,
    platform_commission_pct: clampPct(raw.platform_commission_pct),
    payment_fee_pct: clampPct(raw.payment_fee_pct),
    ad_rate_pct: clampPct(raw.ad_rate_pct),
    return_rate_pct: clampPct(raw.return_rate_pct),
    fba_fee_per_unit_rmb: safeMoney(raw.fba_fee_per_unit_rmb),
    overseas_storage_rmb: safeMoney(raw.overseas_storage_rmb),
    after_sales_cost_rmb: safeMoney(raw.after_sales_cost_rmb),
    channel_tax_rmb: safeMoney(raw.channel_tax_rmb),
    settlement_fee_rmb: safeMoney(raw.settlement_fee_rmb),
    inventory_capital_cost_rmb: safeMoney(raw.inventory_capital_cost_rmb),
    other_channel_cost_rmb: safeMoney(raw.other_channel_cost_rmb)
  };
  const hasCostBasisOverride = costBasisMode === 'manual' || manualBasisCostPerUnitRmb > 0;
  return hasPositiveValue(norm, LAYER_C_FIELDS) || hasCostBasisOverride ? norm : null;
}

function normalizeLayerB(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const norm = {};
  for (const field of LAYER_B_FIELDS) {
    norm[field] = safeMoney(raw[field]);
    norm[`${field}_rule`] = normalizeTradeCostRule(raw[`${field}_rule`] ?? raw[`${field}_allocation_rule`]);
    norm[`${field}_ratio_pct`] = clampPct(raw[`${field}_ratio_pct`] ?? raw[`${field}_allocation_pct`]);
  }
  const rawHsItems = Array.isArray(raw.hs_rebate_items) ? raw.hs_rebate_items : [];
  norm.hs_rebate_items = rawHsItems.slice(0, 200).map((item) => ({
    hs_code: safeText(item?.hs_code).slice(0, 13),
    label: safeText(item?.label ?? item?.name).slice(0, 80),
    base_amount_rmb: safeMoney(item?.base_amount_rmb ?? item?.amount_rmb),
    tax_rebate_rate: clampSignedRebateRate(item?.tax_rebate_rate ?? item?.rebate_rate)
  }));
  const hasHsItems = norm.hs_rebate_items.some((item) =>
    item.base_amount_rmb > 0 || item.tax_rebate_rate !== 0
  );
  const hasTradeRatios = LAYER_B_FIELDS.some((field) => norm[`${field}_ratio_pct`] > 0);
  return hasPositiveValue(norm, LAYER_B_FIELDS) || hasTradeRatios || hasHsItems ? norm : null;
}

function tradeCostAmount(layerBInputs, field, sellAfterDiscountRmb, baseCostRmb) {
  const fixedAmount = safeMoney(layerBInputs?.[field]);
  const ratioPct = clampPct(layerBInputs?.[`${field}_ratio_pct`]);
  const rule = normalizeTradeCostRule(layerBInputs?.[`${field}_rule`]);
  let ratioBaseRmb = 0;
  if (rule === 'sales_pct') {
    ratioBaseRmb = sellAfterDiscountRmb;
  } else if (rule === 'cost_pct') {
    ratioBaseRmb = baseCostRmb;
  }
  const ratioAmountRmb = ratioBaseRmb * (ratioPct / 100);
  return {
    field,
    rule,
    fixed_amount_rmb: fixedAmount,
    ratio_pct: ratioPct,
    ratio_base_rmb: ratioBaseRmb,
    ratio_amount_rmb: ratioAmountRmb,
    amount_rmb: fixedAmount + ratioAmountRmb
  };
}

function normalizeLayerE(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const share = raw.overseas_profit_share_pct ?? raw.transfer_pricing_pct ?? 0;
  const rawEntities = Array.isArray(raw.overseas_entities) && raw.overseas_entities.length > 0
    ? raw.overseas_entities
    : [{
        name: raw.overseas_entity_name || '海外运营主体 1',
        profit_share_pct: 100,
        operating_expense_rmb: raw.entity_compliance_cost_rmb ?? raw.overseas_operating_expense_rmb ?? 0,
        income_tax_pct: raw.overseas_tax_pct ?? 0
      }];
  const entities = rawEntities.map((entity, idx) => ({
    name: String(entity?.name || `海外运营主体 ${idx + 1}`).trim().slice(0, 80),
    profit_share_pct: clampPct(entity?.profit_share_pct ?? (rawEntities.length === 1 ? 100 : 0)),
    operating_expense_rmb: safeMoney(entity?.operating_expense_rmb),
    income_tax_pct: clampPct(entity?.income_tax_pct ?? entity?.tax_pct ?? 0),
    flow_tax_pct: clampPct(entity?.flow_tax_pct ?? 0)
  }));
  const entityExpenseItems = Array.isArray(raw.entity_expense_items)
    ? raw.entity_expense_items.slice(0, 100).map((item, idx) => ({
        label: safeText(item?.label ?? item?.name, `主体费用 ${idx + 1}`).slice(0, 80),
        entity_name: safeText(item?.entity_name ?? item?.entity).slice(0, 80),
        total_amount_rmb: safeMoney(item?.total_amount_rmb),
        allocation_method: normalizeEntityExpenseMethod(item?.allocation_method ?? item?.method).slice(0, 40),
        allocation_pct: clampPct(item?.allocation_pct),
        allocated_amount_rmb: safeMoney(item?.allocated_amount_rmb)
      }))
    : [];
  const norm = {
    overseas_profit_share_pct: clampPct(share),
    transfer_pricing_pct: clampPct(share),
    china_income_tax_pct: clampPct(raw.china_income_tax_pct ?? raw.domestic_tax_pct ?? 25),
    domestic_operating_expense_rmb: safeMoney(raw.domestic_operating_expense_rmb),
    settlement_path: safeText(raw.settlement_path, '平台 → 海外主体 → 国内分红/服务费').slice(0, 120),
    entity_expense_items: entityExpenseItems,
    overseas_entities: entities
  };
  const hasEntityValue = entities.some((entity) =>
    entity.profit_share_pct > 0 || entity.operating_expense_rmb > 0
      || entity.income_tax_pct > 0 || entity.flow_tax_pct > 0
  );
  const hasExpenseItems = entityExpenseItems.some((item) =>
    item.total_amount_rmb > 0 || item.allocated_amount_rmb > 0 || item.allocation_pct > 0
  );
  return (norm.overseas_profit_share_pct > 0
    || norm.china_income_tax_pct > 0
    || norm.domestic_operating_expense_rmb > 0
    || hasEntityValue
    || hasExpenseItems) ? norm : null;
}

function allocatedEntityExpenseItems(layerEInputs) {
  const items = layerEInputs.entity_expense_items.map((item) => {
    const method = normalizeEntityExpenseMethod(item.allocation_method);
    const allocatedRmb = method === 'manual' || method === 'auto_tax'
      ? item.allocated_amount_rmb
      : (ENTITY_EXPENSE_RATIO_METHODS.includes(method)
          ? item.total_amount_rmb * (item.allocation_pct / 100)
          : 0);
    return { ...item, allocation_method: method, allocated_amount_rmb: allocatedRmb };
  });
  const hasExplicitExpenseItems = items.some((item) =>
    item.total_amount_rmb > 0 || item.allocated_amount_rmb > 0 || item.allocation_pct > 0
  );

  if (!hasExplicitExpenseItems && layerEInputs.domestic_operating_expense_rmb > 0) {
    items.push({
      label: '国内运营费用',
      entity_name: '国内主体',
      total_amount_rmb: layerEInputs.domestic_operating_expense_rmb,
      allocation_method: 'legacy_operating_expense',
      allocation_pct: 100,
      allocated_amount_rmb: layerEInputs.domestic_operating_expense_rmb
    });
  }

  if (!hasExplicitExpenseItems) {
    for (const entity of layerEInputs.overseas_entities) {
      if (entity.operating_expense_rmb > 0) {
        items.push({
          label: '运营费用',
          entity_name: entity.name,
          total_amount_rmb: entity.operating_expense_rmb,
          allocation_method: 'legacy_operating_expense',
          allocation_pct: 100,
          allocated_amount_rmb: entity.operating_expense_rmb
        });
      }
    }
  }

  return items;
}

/** banker's rounding to 2 decimals */
export function roundRmb(n) {
  if (!isFiniteNumber(n)) return 0;
  const cents = n * 100;
  const floor = Math.floor(cents);
  const diff = cents - floor;
  if (diff < 0.5) return floor / 100;
  if (diff > 0.5) return (floor + 1) / 100;
  return (floor % 2 === 0 ? floor : floor + 1) / 100;
}

/**
 * Compute a best-effort preview from the same input shape the server accepts.
 * Returns { ok: false, errors: [{field, message}] } if the input is obviously
 * invalid; otherwise { ok: true, totals }. The preview intentionally does
 * NOT throw on partial input — the wizard wants to render "—" placeholders
 * until the user finishes entering each row.
 */
export function previewTotals(input) {
  const errors = [];
  const currency = (input?.currency || 'CNY').toUpperCase();
  if (!SUPPORTED_CURRENCIES.includes(currency)) {
    errors.push({ field: 'currency', message: '不支持的币种' });
  }
  let fx = currency === 'CNY' ? 1 : Number(input?.fx_rate_to_rmb);
  if (!isFiniteNumber(fx) || fx <= 0) {
    if (currency !== 'CNY') errors.push({ field: 'fx_rate_to_rmb', message: '汇率无效' });
    fx = currency === 'CNY' ? 1 : 0;
  }

  const discountPct = Number(input?.discount_pct ?? 0);
  const taxPct = Number(input?.tax_rate_pct ?? 0);
  const taxRebateRate = Number(input?.tax_rebate_rate ?? 0);
  if (!isFiniteNumber(discountPct) || discountPct < 0 || discountPct > 100) {
    errors.push({ field: 'discount_pct', message: '折扣率应在 0-100 之间' });
  }
  if (!isFiniteNumber(taxPct) || taxPct < 0 || taxPct > 100) {
    errors.push({ field: 'tax_rate_pct', message: '税率应在 0-100 之间' });
  }
  if (!isFiniteNumber(taxRebateRate) || taxRebateRate < -1 || taxRebateRate > 1) {
    errors.push({ field: 'tax_rebate_rate', message: '退税率应在 -1 到 1 之间' });
  }

  const lineItems = Array.isArray(input?.line_items) ? input.line_items : [];
  const shared = Array.isArray(input?.shared_costs) ? input.shared_costs : [];

  const totalQty = lineItems.reduce((acc, source) => {
    const qty = Number.parseInt(source?.qty, 10);
    return acc + (Number.isInteger(qty) && qty > 0 ? qty : 0);
  }, 0);

  const layerAInputs = normalizeLayerA(input?.layer_a_data);
  let layerAData = null;
  let layerAFactoryQuoteRaw = 0;
  if (layerAInputs) {
    const materialItems = layerAInputs.material_items.map((row) => {
      const amount = materialAmount(row);
      return { ...row, amount_rmb: roundRmb(amount) };
    });
    const processItems = layerAInputs.process_items.map((row) => {
      const amount = processAmount(row);
      return { ...row, amount_rmb: roundRmb(amount) };
    });
    const materialTotal = layerAInputs.material_items.reduce((acc, row) => acc + materialAmount(row), 0);
    const processTotal = layerAInputs.process_items.reduce((acc, row) => acc + processAmount(row), 0);
    const feeItems = layerAInputs.manufacturing_fees.map((row) => {
      const manufacturingBasis = manufacturingFeeBasisAmount(
        layerAInputs,
        row,
        materialTotal,
        processTotal
      );
      const amount = manufacturingFeeAmount(row, layerAInputs, manufacturingBasis, totalQty);
      return {
        ...row,
        manufacturing_fee_mode: row.manufacturing_fee_mode || layerAInputs.manufacturing_fee_mode,
        manufacturing_fee_allocation_basis: row.manufacturing_fee_allocation_basis
          || layerAInputs.manufacturing_fee_allocation_basis,
        amount_rmb: roundRmb(amount)
      };
    });
    const feeTotal = layerAInputs.manufacturing_fees.reduce(
      (acc, row) => {
        const manufacturingBasis = manufacturingFeeBasisAmount(
          layerAInputs,
          row,
          materialTotal,
          processTotal
        );
        return acc + manufacturingFeeAmount(row, layerAInputs, manufacturingBasis, totalQty);
      },
      0
    );
    const factoryTaxInclusiveCost = materialTotal + processTotal + feeTotal;
    const factoryBaseCost = factoryTaxInclusiveCost;
    const smallBatchUplift = factoryBaseCost * (layerAInputs.small_batch_uplift_pct / 100);
    const factoryCostAfterUplift = factoryBaseCost + smallBatchUplift;
    const factoryQuote = layerAInputs.factory_margin_pct > 0
      ? factoryCostAfterUplift / (1 - layerAInputs.factory_margin_pct / 100)
      : factoryCostAfterUplift;
    const factoryStageProfit = factoryQuote - factoryCostAfterUplift;
    const factoryStageMarginPct = factoryQuote > 0
      ? (factoryStageProfit / factoryQuote) * 100
      : 0;
    const factoryRebate = layerAInputs.factory_rebate_eligible
      ? factoryQuote * layerAInputs.factory_rebate_rate
      : 0;
    layerAFactoryQuoteRaw = factoryQuote;
    layerAData = {
      inputs: {
        ...layerAInputs,
        materials: materialItems,
        material_items: materialItems,
        processes: processItems,
        process_items: processItems,
        manufacturing_fees: feeItems
      },
      material_items: materialItems,
      process_items: processItems,
      manufacturing_fees: feeItems,
      summary: {
        material_total_rmb: roundRmb(materialTotal),
        process_total_rmb: roundRmb(processTotal),
        manufacturing_fee_rmb: roundRmb(feeTotal),
        factory_tax_inclusive_cost_rmb: roundRmb(factoryTaxInclusiveCost),
        small_batch_uplift_rmb: roundRmb(smallBatchUplift),
        factory_cost_after_uplift_rmb: roundRmb(factoryCostAfterUplift),
        factory_output_price_rmb: roundRmb(factoryQuote),
        factory_quote_rmb: roundRmb(factoryQuote),
        factory_gross_profit_rmb: roundRmb(factoryStageProfit),
        factory_stage_profit_rmb: roundRmb(factoryStageProfit),
        factory_stage_margin_pct: Math.round(factoryStageMarginPct * 100) / 100,
        factory_rebate_rmb: roundRmb(factoryRebate),
        factory_net_output_cost_rmb: roundRmb(factoryQuote - factoryRebate),
        total_factory_output_rmb: roundRmb(factoryQuote * totalQty),
        output_term: layerAInputs.output_term,
        factory_handles_rebate: layerAInputs.factory_handles_rebate,
        use_factory_quote_as_cost: layerAInputs.use_factory_quote_as_cost,
        capital_days: layerAInputs.capital_days,
        manufacturing_fee_mode: layerAInputs.manufacturing_fee_mode,
        manufacturing_fee_allocation_basis: layerAInputs.manufacturing_fee_allocation_basis
      }
    };
  }

  const factoryQuoteCostRmb = layerAInputs?.use_factory_quote_as_cost && layerAFactoryQuoteRaw > 0
    ? layerAFactoryQuoteRaw
    : null;

  let sellSubtotalRmb = 0;
  let costSubtotalRmb = 0;
  let lineExtraCostRmb = 0;
  const lines = [];
  const lineRebateRows = [];

  for (let i = 0; i < lineItems.length; i += 1) {
    const li = lineItems[i];
    const qty = Number.parseInt(li?.qty, 10);
    const sell = Number(li?.unit_sell_rmb);
    const manualCost = Number(li?.unit_cost_rmb);
    const cost = factoryQuoteCostRmb ?? manualCost;
    const extra = Number(li?.line_extra_cost_rmb ?? 0);
    const lineTaxRebateRate = li?.tax_rebate_rate == null || li?.tax_rebate_rate === ''
      ? 0
      : Number(li.tax_rebate_rate);
    if (!isFiniteNumber(lineTaxRebateRate) || lineTaxRebateRate < -1 || lineTaxRebateRate > 1) {
      errors.push({ field: `line_items[${i}].tax_rebate_rate`, message: '商品行退税率应在 -1 到 1 之间' });
    }

    const okRow = Number.isInteger(qty) && qty > 0
      && isFiniteNumber(sell) && sell >= 0
      && isFiniteNumber(cost) && cost >= 0
      && isFiniteNumber(extra) && extra >= 0
      && isFiniteNumber(lineTaxRebateRate) && lineTaxRebateRate >= -1 && lineTaxRebateRate <= 1;

    const lineSellRmb = okRow ? sell * qty : 0;
    const lineCostRmb = okRow ? (cost * qty) + extra : 0;
    const lineExtraRmb = okRow ? extra : 0;

    sellSubtotalRmb += lineSellRmb;
    costSubtotalRmb += okRow ? cost * qty : 0;
    lineExtraCostRmb += lineExtraRmb;

    const lineMarginRmb = lineSellRmb - lineCostRmb;
    const lineMarginPct = lineSellRmb > 0 ? (lineMarginRmb / lineSellRmb) * 100 : 0;
    if (okRow && (safeText(li?.hs_code) || lineTaxRebateRate !== 0)) {
      lineRebateRows.push({
        idx: i,
        hs_code: safeText(li?.hs_code).slice(0, 13),
        label: safeText(li?.description || li?.sku, `商品行 ${i + 1}`).slice(0, 80),
        base_amount_rmb: lineSellRmb,
        tax_rebate_rate: clampSignedRebateRate(lineTaxRebateRate),
        tax_rebate_source: safeText(li?.tax_rebate_source).slice(0, 40)
      });
    }

    lines.push({
      idx: i,
      valid: okRow,
      hs_code: safeText(li?.hs_code).slice(0, 13),
      tax_rebate_rate: clampSignedRebateRate(lineTaxRebateRate),
      tax_rebate_source: safeText(li?.tax_rebate_source).slice(0, 40) || null,
      unit_cost_rmb: roundRmb(cost),
      cost_source: factoryQuoteCostRmb == null ? 'line_item' : 'factory_quote',
      line_sell_rmb: roundRmb(lineSellRmb),
      line_cost_rmb: roundRmb(lineCostRmb),
      line_margin_rmb: roundRmb(lineMarginRmb),
      line_margin_pct: Math.round(lineMarginPct * 100) / 100
    });
  }

  let sharedCostsRmb = 0;
  for (const sc of shared) {
    const amt = Number(sc?.amount_rmb);
    if (isFiniteNumber(amt) && amt >= 0) sharedCostsRmb += amt;
  }

  const safeDiscount = isFiniteNumber(discountPct) ? Math.max(0, Math.min(100, discountPct)) : 0;
  const safeTax = isFiniteNumber(taxPct) ? Math.max(0, Math.min(100, taxPct)) : 0;
  const discountRmb = sellSubtotalRmb * (safeDiscount / 100);
  const sellAfterDiscountRmb = sellSubtotalRmb - discountRmb;
  const taxRmb = sellAfterDiscountRmb * (safeTax / 100);
  const discountFactor = sellSubtotalRmb > 0 ? sellAfterDiscountRmb / sellSubtotalRmb : 1;
  const lineHsRebateItems = lineRebateRows.map((item) => {
    const baseRmb = item.base_amount_rmb * discountFactor;
    const itemRebateRmb = baseRmb * item.tax_rebate_rate;
    return {
      idx: item.idx,
      hs_code: item.hs_code,
      label: item.label,
      base_amount_rmb: roundRmb(baseRmb),
      tax_rebate_rate: item.tax_rebate_rate,
      ...(item.tax_rebate_source ? { tax_rebate_source: item.tax_rebate_source } : {}),
      rebate_amount_rmb: roundRmb(itemRebateRmb),
      _rebate_rmb: itemRebateRmb
    };
  });

  const layerBInputs = normalizeLayerB(input?.layer_b_data);
  let tradeCostRmb = 0;
  let layerBData = null;
  const safeRebateRate = clampSignedRebateRate(taxRebateRate);
  const rebateTreatment = layerAInputs?.rebate_treatment || normalizeRebateTreatment(
    input?.layer_a_data?.rebate_treatment,
    input?.layer_a_data?.rebate_owner
  );
  const rebateOwner = REBATE_OWNER_BY_TREATMENT[rebateTreatment] || 'none';
  let rebateRmb = lineHsRebateItems.length > 0
    ? lineHsRebateItems.reduce((acc, item) => acc + item._rebate_rmb, 0)
    : sellAfterDiscountRmb * safeRebateRate;
  const rebateBuckets = (items) => ({
    positive: items.reduce((acc, item) => acc + Math.max(0, item._rebate_rmb), 0),
    negativeCost: items.reduce((acc, item) => acc + Math.max(0, -item._rebate_rmb), 0)
  });
  let rebateBucket = lineHsRebateItems.length > 0
    ? rebateBuckets(lineHsRebateItems)
    : { positive: Math.max(0, rebateRmb), negativeCost: Math.max(0, -rebateRmb) };
  if (layerBInputs) {
    const breakdown = {};
    const breakdown_items = [];
    const tradeRatioBaseCostRmb = costSubtotalRmb + lineExtraCostRmb + sharedCostsRmb;
    for (const field of LAYER_B_FIELDS) {
      const item = tradeCostAmount(layerBInputs, field, sellAfterDiscountRmb, tradeRatioBaseCostRmb);
      tradeCostRmb += item.amount_rmb;
      breakdown[field] = roundRmb(item.amount_rmb);
      breakdown[`${field}_fixed_amount_rmb`] = roundRmb(item.fixed_amount_rmb);
      breakdown[`${field}_ratio_pct`] = item.ratio_pct;
      breakdown[`${field}_ratio_amount_rmb`] = roundRmb(item.ratio_amount_rmb);
      breakdown[`${field}_rule`] = item.rule;
      breakdown_items.push({
        field,
        rule: item.rule,
        fixed_amount_rmb: roundRmb(item.fixed_amount_rmb),
        ratio_pct: item.ratio_pct,
        ratio_base_rmb: roundRmb(item.ratio_base_rmb),
        ratio_amount_rmb: roundRmb(item.ratio_amount_rmb),
        amount_rmb: roundRmb(item.amount_rmb)
      });
    }
    const legacyHsRebateItems = Array.isArray(layerBInputs.hs_rebate_items)
      ? layerBInputs.hs_rebate_items.map((item, idx) => {
          const baseRmb = item.base_amount_rmb;
          const itemRebateRmb = baseRmb * item.tax_rebate_rate;
          return {
            idx,
            hs_code: item.hs_code,
            label: item.label,
            base_amount_rmb: roundRmb(baseRmb),
            tax_rebate_rate: item.tax_rebate_rate,
            ...(item.tax_rebate_source ? { tax_rebate_source: item.tax_rebate_source } : {}),
            rebate_amount_rmb: roundRmb(itemRebateRmb),
            _rebate_rmb: itemRebateRmb
          };
        })
      : [];
    const hsRebateItems = lineHsRebateItems.length > 0 ? lineHsRebateItems : legacyHsRebateItems;
    if (lineHsRebateItems.length === 0 && legacyHsRebateItems.length > 0) {
      rebateRmb = hsRebateItems.reduce((acc, item) => acc + item._rebate_rmb, 0);
      rebateBucket = rebateBuckets(hsRebateItems);
    }
    layerBData = {
      inputs: layerBInputs,
      hs_rebate_items: hsRebateItems.map(({ _rebate_rmb, ...item }) => item),
      breakdown,
      breakdown_items,
      summary: {
        trade_cost_rmb: roundRmb(tradeCostRmb),
        trade_cost_per_unit_rmb: roundRmb(totalQty > 0 ? tradeCostRmb / totalQty : 0),
        export_rebate_rmb: roundRmb(rebateTreatment === 'none' ? 0 : rebateRmb),
        raw_export_rebate_rmb: roundRmb(rebateRmb),
        rebate_treatment: rebateTreatment,
        rebate_owner: rebateOwner,
        hs_rebate_items_count: hsRebateItems.length
      }
    };
  } else if (lineHsRebateItems.length > 0) {
    layerBData = {
      inputs: null,
      hs_rebate_items: lineHsRebateItems.map(({ _rebate_rmb, ...item }) => item),
      breakdown: {},
      breakdown_items: [],
      summary: {
        trade_cost_rmb: 0,
        trade_cost_per_unit_rmb: 0,
        export_rebate_rmb: roundRmb(rebateTreatment === 'none' ? 0 : rebateRmb),
        raw_export_rebate_rmb: roundRmb(rebateRmb),
        rebate_treatment: rebateTreatment,
        rebate_owner: rebateOwner,
        hs_rebate_items_count: lineHsRebateItems.length
      }
    };
  }

  const baseCostRmb = costSubtotalRmb + lineExtraCostRmb + sharedCostsRmb + tradeCostRmb;
  const effectiveRebateRmb = rebateTreatment === 'none' ? 0 : rebateRmb;
  const positiveRebateRmb = rebateTreatment === 'none' ? 0 : rebateBucket.positive;
  const negativeRebateCostRmb = rebateTreatment === 'none' ? 0 : rebateBucket.negativeCost;
  const entityRebateRmb = rebateOwner === 'entity' ? effectiveRebateRmb : 0;
  const effectiveBaseCostRmb = baseCostRmb + negativeRebateCostRmb;
  const totalRevenueRmb = sellAfterDiscountRmb + taxRmb;
  const tradeQuoteRmb = sellAfterDiscountRmb;
  const tradeQuotePerUnitRmb = totalQty > 0 ? tradeQuoteRmb / totalQty : 0;
  const tradeNegativeRebateCostRmb = rebateOwner === 'trade' ? negativeRebateCostRmb : 0;
  const tradeStageCostBasisRmb = costSubtotalRmb + lineExtraCostRmb
    + sharedCostsRmb + tradeCostRmb + tradeNegativeRebateCostRmb;
  const tradeStageProfitRmb = tradeQuoteRmb - tradeStageCostBasisRmb;
  const tradeStageMarginPct = tradeQuoteRmb > 0
    ? (tradeStageProfitRmb / tradeQuoteRmb) * 100
    : 0;
  if (layerBData?.summary) {
    layerBData.summary.trade_quote_rmb = roundRmb(tradeQuoteRmb);
    layerBData.summary.trade_quote_per_unit_rmb = roundRmb(tradeQuotePerUnitRmb);
    layerBData.summary.trade_stage_profit_rmb = roundRmb(tradeStageProfitRmb);
    layerBData.summary.trade_stage_margin_pct = Math.round(tradeStageMarginPct * 100) / 100;
  }

  const layerCInputs = normalizeLayerC(input?.layer_c_data);
  let channelCostRmb = 0;
  let channelCostBasisRmb = effectiveBaseCostRmb;
  let channelPricePerUnitRmb = totalQty > 0 ? sellAfterDiscountRmb / totalQty : 0;
  let layerCData = null;
  if (layerCInputs) {
    const channelCostBasisMode = layerCInputs.cost_basis_mode || 'inherit_trade';
    channelCostBasisRmb = channelCostBasisMode === 'manual'
      ? layerCInputs.manual_basis_cost_per_unit_rmb * totalQty
      : effectiveBaseCostRmb;
    channelPricePerUnitRmb = totalQty > 0 ? sellAfterDiscountRmb / totalQty : 0;
    const commissionRmb = sellAfterDiscountRmb * (layerCInputs.platform_commission_pct / 100);
    const paymentFeeRmb = sellAfterDiscountRmb * (layerCInputs.payment_fee_pct / 100);
    const adCostRmb = sellAfterDiscountRmb * (layerCInputs.ad_rate_pct / 100);
    const returnCostRmb = sellAfterDiscountRmb * (layerCInputs.return_rate_pct / 100);
    const fbaCostRmb = layerCInputs.fba_fee_per_unit_rmb * totalQty;
    const storageCostRmb = layerCInputs.overseas_storage_rmb;
    const afterSalesCostRmb = layerCInputs.after_sales_cost_rmb;
    const channelTaxRmb = layerCInputs.channel_tax_rmb;
    const settlementFeeRmb = layerCInputs.settlement_fee_rmb;
    const inventoryCapitalCostRmb = layerCInputs.inventory_capital_cost_rmb;
    const otherCostRmb = layerCInputs.other_channel_cost_rmb;
    channelCostRmb = commissionRmb + paymentFeeRmb + adCostRmb
      + returnCostRmb + fbaCostRmb + storageCostRmb
      + afterSalesCostRmb + channelTaxRmb + settlementFeeRmb
      + inventoryCapitalCostRmb + otherCostRmb;
    const channelProfitRmb = sellAfterDiscountRmb - channelCostBasisRmb - channelCostRmb;
    const channelMarginPct = sellAfterDiscountRmb > 0
      ? (channelProfitRmb / sellAfterDiscountRmb) * 100
      : 0;
    layerCData = {
      inputs: {
        ...layerCInputs,
        cost_basis_mode: channelCostBasisMode,
        manual_basis_cost_per_unit_rmb: roundRmb(layerCInputs.manual_basis_cost_per_unit_rmb)
      },
      basis: {
        total_qty: totalQty,
        sell_after_discount_rmb: roundRmb(sellAfterDiscountRmb),
        channel_cost_basis_rmb: roundRmb(channelCostBasisRmb)
      },
      breakdown: {
        platform_commission_rmb: roundRmb(commissionRmb),
        payment_fee_rmb: roundRmb(paymentFeeRmb),
        ad_cost_rmb: roundRmb(adCostRmb),
        return_loss_rmb: roundRmb(returnCostRmb),
        fba_fulfillment_rmb: roundRmb(fbaCostRmb),
        overseas_storage_rmb: roundRmb(storageCostRmb),
        after_sales_cost_rmb: roundRmb(afterSalesCostRmb),
        channel_tax_rmb: roundRmb(channelTaxRmb),
        settlement_fee_rmb: roundRmb(settlementFeeRmb),
        inventory_capital_cost_rmb: roundRmb(inventoryCapitalCostRmb),
        other_channel_cost_rmb: roundRmb(otherCostRmb)
      },
      summary: {
        channel_cost_rmb: roundRmb(channelCostRmb),
        channel_cost_basis_rmb: roundRmb(channelCostBasisRmb),
        channel_price_per_unit_rmb: roundRmb(channelPricePerUnitRmb),
        channel_net_profit_rmb: roundRmb(channelProfitRmb),
        channel_margin_pct: Math.round(channelMarginPct * 100) / 100
      }
    };
  }

  const profitBeforeEntityRmb = sellAfterDiscountRmb + positiveRebateRmb - channelCostBasisRmb - channelCostRmb;
  const layerEInputs = normalizeLayerE(input?.layer_e_data);
  let entityCostRmb = 0;
  let layerEData = null;
  if (layerEInputs) {
    const entityExpenseItems = allocatedEntityExpenseItems(layerEInputs);
    const entityExpenseTotalRmb = entityExpenseItems.reduce(
      (acc, item) => acc + item.allocated_amount_rmb, 0
    );
    const legacyDomesticOperatingExpenseRmb = entityExpenseItems
      .filter((item) => item.allocation_method === 'legacy_operating_expense' && item.entity_name === '国内主体')
      .reduce((acc, item) => acc + item.allocated_amount_rmb, 0);
    const legacyOverseasOperatingExpenseRmb = entityExpenseItems
      .filter((item) => item.allocation_method === 'legacy_operating_expense' && item.entity_name !== '国内主体')
      .reduce((acc, item) => acc + item.allocated_amount_rmb, 0);
    const distributableProfitPoolRmb = profitBeforeEntityRmb - entityExpenseTotalRmb;
    const overseasGrossProfitRmb = distributableProfitPoolRmb * (layerEInputs.overseas_profit_share_pct / 100);
    const domesticGrossProfitRmb = distributableProfitPoolRmb - overseasGrossProfitRmb;
    const domesticTaxableRmb = Math.max(0, domesticGrossProfitRmb);
    const chinaTaxRmb = domesticTaxableRmb * (layerEInputs.china_income_tax_pct / 100);

    const entityShareTotal = layerEInputs.overseas_entities.reduce(
      (acc, entity) => acc + Number(entity.profit_share_pct || 0), 0
    ) || 100;
    let overseasTaxCostRmb = 0;
    let overseasFlowTaxRmb = 0;
    const overseasEntities = layerEInputs.overseas_entities.map((entity) => {
      const sharePct = Number(entity.profit_share_pct || 0);
      const grossRmb = overseasGrossProfitRmb * (sharePct / entityShareTotal);
      const taxableRmb = Math.max(0, grossRmb);
      const flowTaxRmb = taxableRmb * (entity.flow_tax_pct / 100);
      const incomeTaxRmb = taxableRmb * (entity.income_tax_pct / 100);
      overseasTaxCostRmb += incomeTaxRmb;
      overseasFlowTaxRmb += flowTaxRmb;
      return {
        ...entity,
        gross_profit_rmb: roundRmb(grossRmb),
        operating_expense_rmb: roundRmb(entity.operating_expense_rmb),
        taxable_profit_rmb: roundRmb(taxableRmb),
        flow_tax_rmb: roundRmb(flowTaxRmb),
        income_tax_rmb: roundRmb(incomeTaxRmb),
        net_profit_rmb: roundRmb(grossRmb - flowTaxRmb - incomeTaxRmb)
      };
    });
    entityCostRmb = entityExpenseTotalRmb + chinaTaxRmb + overseasTaxCostRmb + overseasFlowTaxRmb;
    const groupNetProfitRmb = profitBeforeEntityRmb - entityCostRmb;
    const groupNetMarginPct = sellAfterDiscountRmb > 0
      ? (groupNetProfitRmb / sellAfterDiscountRmb) * 100
      : 0;
    layerEData = {
      inputs: layerEInputs,
      basis: {
        channel_cost_rmb: roundRmb(channelCostRmb),
        profit_after_channel_rmb: roundRmb(profitBeforeEntityRmb),
        profit_before_entity_rmb: roundRmb(profitBeforeEntityRmb),
        entity_node_rebate_rmb: roundRmb(entityRebateRmb),
        entity_expense_total_rmb: roundRmb(entityExpenseTotalRmb),
        distributable_profit_pool_rmb: roundRmb(distributableProfitPoolRmb)
      },
      entity_expense_items: entityExpenseItems.map((item) => ({
        ...item,
        total_amount_rmb: roundRmb(item.total_amount_rmb),
        allocated_amount_rmb: roundRmb(item.allocated_amount_rmb)
      })),
      split: {
        domestic_gross_profit_rmb: roundRmb(domesticGrossProfitRmb),
        domestic_operating_expense_rmb: roundRmb(legacyDomesticOperatingExpenseRmb),
        domestic_taxable_profit_rmb: roundRmb(domesticTaxableRmb),
        china_income_tax_rmb: roundRmb(chinaTaxRmb),
        overseas_gross_profit_rmb: roundRmb(overseasGrossProfitRmb),
        overseas_operating_expense_rmb: roundRmb(legacyOverseasOperatingExpenseRmb),
        overseas_flow_tax_rmb: roundRmb(overseasFlowTaxRmb),
        overseas_income_tax_rmb: roundRmb(overseasTaxCostRmb),
        overseas_entities: overseasEntities
      },
      summary: {
        entity_cost_rmb: roundRmb(entityCostRmb),
        entity_node_rebate_rmb: roundRmb(entityRebateRmb),
        group_net_profit_rmb: roundRmb(groupNetProfitRmb),
        group_net_margin_pct: Math.round(groupNetMarginPct * 100) / 100
      }
    };
  }

  const totalCostRmb = channelCostBasisRmb + channelCostRmb + entityCostRmb;
  const grossProfitRmb = sellAfterDiscountRmb + positiveRebateRmb - totalCostRmb;
  const marginPct = sellAfterDiscountRmb > 0
    ? (grossProfitRmb / sellAfterDiscountRmb) * 100
    : 0;

  return {
    ok: errors.length === 0,
    errors,
    totals: {
      currency,
      fx_rate_to_rmb: fx || 0,
      discount_pct: safeDiscount,
      tax_rate_pct: safeTax,
      tax_rebate_rate: safeRebateRate,
      sell_subtotal_rmb: roundRmb(sellSubtotalRmb),
      discount_amount_rmb: roundRmb(discountRmb),
      sell_after_discount_rmb: roundRmb(sellAfterDiscountRmb),
      tax_amount_rmb: roundRmb(taxRmb),
      total_revenue_rmb: roundRmb(totalRevenueRmb),
      total_revenue_fx: fx > 0
        ? roundRmb(totalRevenueRmb / fx)
        : roundRmb(totalRevenueRmb),
      cost_subtotal_rmb: roundRmb(costSubtotalRmb),
      line_extra_cost_rmb: roundRmb(lineExtraCostRmb),
      shared_cost_rmb: roundRmb(sharedCostsRmb),
      factory_quote_rmb: layerAData?.summary?.factory_quote_rmb ?? null,
      factory_cost_after_uplift_rmb: layerAData?.summary?.factory_cost_after_uplift_rmb ?? null,
      factory_stage_profit_rmb: layerAData?.summary?.factory_stage_profit_rmb ?? null,
      factory_stage_margin_pct: layerAData?.summary?.factory_stage_margin_pct ?? null,
      trade_cost_rmb: roundRmb(tradeCostRmb),
      trade_cost_per_unit_rmb: roundRmb(totalQty > 0 ? tradeCostRmb / totalQty : 0),
      trade_quote_rmb: roundRmb(tradeQuoteRmb),
      trade_quote_per_unit_rmb: roundRmb(tradeQuotePerUnitRmb),
      trade_stage_profit_rmb: roundRmb(tradeStageProfitRmb),
      trade_stage_margin_pct: Math.round(tradeStageMarginPct * 100) / 100,
      export_rebate_rmb: roundRmb(effectiveRebateRmb),
      raw_export_rebate_rmb: roundRmb(rebateRmb),
      rebate_treatment: rebateTreatment,
      rebate_owner: rebateOwner,
      channel_cost_rmb: roundRmb(channelCostRmb),
      channel_cost_basis_rmb: roundRmb(channelCostBasisRmb),
      channel_price_per_unit_rmb: roundRmb(channelPricePerUnitRmb),
      channel_margin_pct: layerCData?.summary?.channel_margin_pct ?? null,
      profit_before_entity_rmb: roundRmb(profitBeforeEntityRmb),
      entity_cost_rmb: roundRmb(entityCostRmb),
      group_net_profit_rmb: roundRmb(grossProfitRmb),
      total_cost_rmb: roundRmb(totalCostRmb),
      gross_profit_rmb: roundRmb(grossProfitRmb),
      margin_pct: Math.round(marginPct * 100) / 100,
      layer_a_data: layerAData,
      layer_b_data: layerBData,
      layer_c_data: layerCData,
      layer_e_data: layerEData,
      lines
    }
  };
}

/** Format a RMB amount for table cells. null → em dash. */
export function formatRmb(n) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('zh-CN', {
    style: 'currency', currency: 'CNY',
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

/** Format a foreign-currency amount for the display-only FX cell. */
export function formatFx(n, currency) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  try {
    return v.toLocaleString(undefined, {
      style: 'currency', currency,
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  } catch {
    return `${currency} ${v.toFixed(2)}`;
  }
}
